import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["description", "name", "providerName", "actions", "certified", "imageSrcBig", "imageSrcSmall", "installed", "linkMode", "listingHref", "metaBadges", "onActionClick", "onLinkClick", "ribbonText", "ribbonUse"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import { HubspotCertificationIcon } from 'foundations-assets';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Image } from 'foundations-components/transitional/components/Image';
import { Ribbon } from 'foundations-components/transitional/components/Ribbon';
import { StatusTag } from 'foundations-components/transitional/components/StatusTag';
import styled from 'foundations-theming/utils';
import { useMemo, useState } from 'react';
import { useLazyImage } from '../LazyImageProvider';
import { ListingActionMenu } from '../ListingActionMenu';
import { PolymorphicLink } from '../PolymorphicLink';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledActionMenuWrapper = styled.div.withConfig({
  displayName: "EcosystemListingCard__StyledActionMenuWrapper",
  componentId: "sc-1ul8kma-0"
})(["position:absolute;inset-inline-end:", ";inset-block-start:", ";opacity:", ";:focus-within{opacity:1;}"], ({
  theme
}) => theme.space['600'], ({
  theme
}) => theme.space['600'], ({
  isHovered
}) => isHovered ? 1 : 0);
export function EcosystemListingCard(_ref) {
  var _sName$lastWord;
  let {
      description,
      name,
      providerName,
      actions = [],
      certified = false,
      imageSrcBig,
      imageSrcSmall,
      installed,
      linkMode = 'default',
      listingHref,
      metaBadges = [],
      onActionClick = () => {},
      onLinkClick,
      ribbonText,
      ribbonUse
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const lazyImage = useLazyImage();
  const [isHovered, setIsHovered] = useState(false);
  const sName = useMemo(() => splitName(name), [name]);
  return /*#__PURE__*/_jsxs(CardWrapper, Object.assign({
    compact: true
    // this is set to "ecosystem-app-card" to make for easy migration off our legacy components
    ,
    "data-test-id": "ecosystem-app-card",
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false)
  }, props, {
    children: [ribbonText && /*#__PURE__*/_jsx(Ribbon, {
      use: ribbonUse,
      position: "left",
      children: ribbonText
    }), /*#__PURE__*/_jsx(PolymorphicLink, {
      linkMode: linkMode,
      href: listingHref,
      onClick: onLinkClick,
      tabIndex: -1,
      children: /*#__PURE__*/_jsxs(View, {
        backgroundColor: "fill-surface-recessed",
        blockSize: "164px",
        borderRadius: "200",
        margin: "600",
        marginBlockEnd: "0",
        overflow: "hidden",
        position: "relative",
        children: [imageSrcBig && /*#__PURE__*/_jsx(Image, {
          blockSize: "164px",
          inlineSize: "100%",
          loading: lazyImage,
          objectFit: "cover",
          src: imageSrcBig
        }), imageSrcSmall && /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          inset: "0",
          justifyContent: "center",
          position: "absolute",
          children: /*#__PURE__*/_jsx(Image, {
            blockSize: "100px",
            loading: lazyImage,
            src: imageSrcSmall
          })
        })]
      })
    }), /*#__PURE__*/_jsxs(CardSection, {
      flexGrow: 1,
      display: "flex",
      flexDirection: "column",
      paddingBlockStart: "500",
      children: [/*#__PURE__*/_jsx(PolymorphicLink, {
        href: listingHref,
        linkMode: linkMode,
        onClick: onLinkClick,
        use: "unstyled",
        children: /*#__PURE__*/_jsxs(Text, {
          as: "h4",
          marginBlockEnd: "50",
          overflowWrap: "break-word",
          children: [sName.allButLastWord, /*#__PURE__*/_jsxs("span", {
            style: {
              whiteSpace: (_sName$lastWord = sName.lastWord) !== null && _sName$lastWord !== void 0 && _sName$lastWord.length && sName.lastWord.length > 16 ? 'normal' : 'nowrap'
            },
            children: [sName.lastWord, certified && /*#__PURE__*/_jsx(IconHolder, {
              icon: HubspotCertificationIcon,
              color: "fill-brand-default",
              padding: "0",
              marginInlineStart: "100"
            })]
          })]
        })
      }), actions.length > 0 &&
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(StyledActionMenuWrapper, {
        isHovered: isHovered,
        children: /*#__PURE__*/_jsx(ListingActionMenu, {
          actions: actions,
          onActionClick: onActionClick
        })
      }), /*#__PURE__*/_jsx(Text, {
        as: "small",
        color: "text-core-subtle",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "trellisEcosystem.appCard.providerName",
          options: {
            providerName
          }
        })
      }), /*#__PURE__*/_jsx(Text, {
        marginBlock: "300",
        children: description
      }), /*#__PURE__*/_jsxs(Flex, {
        alignItems: "end",
        gap: "200",
        marginBlockStart: "auto",
        children: [metaBadges.map(metaBadge => /*#__PURE__*/_jsx(PolymorphicLink, {
          href: metaBadge.href,
          linkMode: linkMode,
          children: /*#__PURE__*/_jsx(Badge, {
            use: "koala",
            children: /*#__PURE__*/_jsxs(Flex, {
              alignItems: "center",
              gap: "100",
              children: [metaBadge.label, metaBadge.icon]
            })
          })
        }, metaBadge.label)), installed && /*#__PURE__*/_jsx(StatusTag, {
          use: "success",
          marginInlineStart: "100",
          marginBlockEnd: "50",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "trellisEcosystem.appCard.installed"
          })
        })]
      })]
    })]
  }));
}
function splitName(name) {
  const split = name.split(/\s+/);
  let allButLastWord = split.slice(0, -1).join(' ');
  if (split.length > 1) allButLastWord += ' ';
  return {
    allButLastWord,
    lastWord: split.pop()
  };
}